/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  MousePointer,
  Hand,
  Type,
  StickyNote,
  Square,
  Circle,
  Triangle,
  ArrowUpRight,
  PenTool,
  Eraser,
  PaintBucket,
  MessageSquare,
  Image as ImageIcon,
  Maximize,
  Undo2,
  Redo2,
  Trash2,
  Table,
  ChevronRight,
  Highlighter,
  CaseSensitive,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Table2,
  ChevronDown,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Baseline
} from 'lucide-react';
import { ToolType, CanvasSheetSize, WhiteboardElement } from '../WhiteboardTypes';
import { BrushSelector } from './BrushSelector';
import { EraserControl } from './EraserControl';

interface WhiteboardToolbarProps {
  activeTool: ToolType;
  setActiveTool: (tool: ToolType) => void;
  onClear: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  gridVisible: boolean;
  setGridVisible: (visible: boolean) => void;
  sheetMode: CanvasSheetSize;
  onOpenSheetSettings: () => void;
  activeColor: string;
  setActiveColor: (color: string) => void;
  toolbarPosition: 'left' | 'bottom';
  setToolbarPosition: (pos: 'left' | 'bottom') => void;
  selectedElement?: WhiteboardElement | null;
  onUpdateElement?: (id: string, updates: Partial<WhiteboardElement>) => void;
  elements?: WhiteboardElement[];
  onAddElement?: (el: WhiteboardElement) => void;
  brushWidth?: number;
  setBrushWidth?: (width: number) => void;
  eraserWidth?: number;
  setEraserWidth?: (width: number) => void;
  brushColors?: string[];
}

const PRESET_COLORS = [
  '#000000', // Black
  '#ef4444', // Red
  '#3b82f6', // Ocean Blue
  '#10b981', // Forest Green
  '#f59e0b', // Amber/Yellow
  '#8b5cf6', // Violet
  '#ec4899', // Hot Pink
  '#64748b'  // Slate Gray
];

export const WhiteboardToolbar: React.FC<WhiteboardToolbarProps> = ({
  activeTool,
  setActiveTool,
  onClear,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  activeColor,
  setActiveColor,
  toolbarPosition,
  selectedElement,
  onUpdateElement,
  elements,
  onAddElement,
  brushWidth,
  setBrushWidth,
  eraserWidth,
  setEraserWidth,
  brushColors
}) => {
  const [showColorPopover, setShowColorPopover] = useState(false);
  const [showShapesDropdown, setShowShapesDropdown] = useState(false);
  const [showLinesDropdown, setShowLinesDropdown] = useState(false);

  // Styling popover states
  const [activePopover, setActivePopover] = useState<'textOptions' | 'align' | 'table' | null>(null);

  // default styling
  const [fontFamily, setFontFamily] = useState('"Inter", sans-serif');
  const [isBold, setIsBold] = useState(true);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left');
  const [fontSize, setFontSize] = useState(14);
  const [lineHeight, setLineHeight] = useState(20);
  
  // Right-click hint tooltip for drawing tools
  const [rightClickHintTool, setRightClickHintTool] = useState<string | null>(null);
  const rightClickHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownRightClickHints = useRef<Set<string>>(new Set());

  // Drawing tool customization popover
  const [showDrawingCustomize, setShowDrawingCustomize] = useState<'pen' | 'highlighter' | 'eraser' | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) {
        setShowColorPopover(false);
        setShowShapesDropdown(false);
        setShowLinesDropdown(false);
        setActivePopover(null);
        setShowDrawingCustomize(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // When activeTool changes, close all menus/popovers
    setShowColorPopover(false);
    setShowShapesDropdown(false);
    setShowLinesDropdown(false);
    setActivePopover(null);

    if (activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser') {
      setShowDrawingCustomize(activeTool);
    } else {
      setShowDrawingCustomize(null);
    }
  }, [activeTool]);

  const handleToolClick = (tool: ToolType) => {
    if (activeTool === tool) {
      const isDrawingTool = tool === 'pen' || tool === 'highlighter' || tool === 'eraser';
      if (isDrawingTool) {
        const next = showDrawingCustomize === tool ? null : (tool as 'pen' | 'highlighter' | 'eraser');
        setShowDrawingCustomize(next);
        if (next) {
          setShowColorPopover(false);
          setShowShapesDropdown(false);
          setShowLinesDropdown(false);
          setActivePopover(null);
        }
      }
    } else {
      setActiveTool(tool);
    }
  };
  
  // table state
  const [hoverGridRow, setHoverGridRow] = useState(3);
  const [hoverGridCol, setHoverGridCol] = useState(3);

  const popupRef = useRef<HTMLDivElement>(null);

  const showRightClickHint = (toolName: string) => {
    if (shownRightClickHints.current.has(toolName)) return;
    shownRightClickHints.current.add(toolName);
    setRightClickHintTool(toolName);
    if (rightClickHintTimer.current) clearTimeout(rightClickHintTimer.current);
    rightClickHintTimer.current = setTimeout(() => setRightClickHintTool(null), 2500);
  };

  const handleGridPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
    const cell = target?.closest<HTMLButtonElement>('[data-table-cell="true"]');

    if (!cell || !popupRef.current?.contains(cell)) return;

    const row = Number(cell.dataset.row);
    const col = Number(cell.dataset.col);
    if (Number.isFinite(row) && Number.isFinite(col)) {
      setHoverGridRow(row);
      setHoverGridCol(col);
    }
  };

  const handleCellPointerDown = (
    event: React.PointerEvent<HTMLButtonElement>,
    row: number,
    col: number
  ) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    setHoverGridRow(row);
    setHoverGridCol(col);
    handleInsertTable(row, col);
  };

  useEffect(() => {
    if (selectedElement) {
      if (selectedElement.fontFamily) setFontFamily(selectedElement.fontFamily);
      setIsBold(selectedElement.fontWeight === 'bold');
      setIsItalic(selectedElement.fontStyle === 'italic');
      setIsUnderline(selectedElement.textDecoration?.includes('underline') || false);
      setIsStrikethrough(selectedElement.textDecoration?.includes('line-through') || false);
      if (selectedElement.textAlign) setTextAlign(selectedElement.textAlign as any);
      if (selectedElement.fontSize) setFontSize(selectedElement.fontSize);
      if (selectedElement.lineHeight) setLineHeight(selectedElement.lineHeight);
    }
  }, [selectedElement]);

  const togglePopover = (type: typeof activePopover) => {
    setActivePopover((prev) => {
      const next = prev === type ? null : type;
      if (next) {
        setShowDrawingCustomize(null);
        setShowColorPopover(false);
        setShowShapesDropdown(false);
        setShowLinesDropdown(false);
      }
      return next;
    });
  };

  const applyStyle = (property: string, value: any) => {
    if (selectedElement && onUpdateElement) {
      const updates: Partial<WhiteboardElement> = { [property]: value };
      if (property === 'fontFamily') updates.fontFamily = value;
      if (property === 'textAlign') updates.textAlign = value;
      if (property === 'fontSize') updates.fontSize = Number(value);
      if (property === 'lineHeight') updates.lineHeight = Number(value);
      onUpdateElement(selectedElement.id, updates);
    } else {
      if (property === 'fontFamily') setFontFamily(value);
      if (property === 'textAlign') setTextAlign(value);
      if (property === 'fontSize') setFontSize(Number(value));
      if (property === 'lineHeight') setLineHeight(Number(value));
    }
  };

  const handleToggleStyle = (type: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    if (type === 'bold') {
      const next = !isBold;
      setIsBold(next);
      if (selectedElement && onUpdateElement) onUpdateElement(selectedElement.id, { fontWeight: next ? 'bold' : 'normal' });
    } else if (type === 'italic') {
      const next = !isItalic;
      setIsItalic(next);
      if (selectedElement && onUpdateElement) onUpdateElement(selectedElement.id, { fontStyle: next ? 'italic' : 'normal' });
    } else if (type === 'underline' || type === 'strikethrough') {
      let u = type === 'underline' ? !isUnderline : isUnderline;
      let s = type === 'strikethrough' ? !isStrikethrough : isStrikethrough;
      
      if (type === 'underline') setIsUnderline(u);
      if (type === 'strikethrough') setIsStrikethrough(s);

      let dec = 'none';
      if (u && s) dec = 'underline line-through';
      else if (u) dec = 'underline';
      else if (s) dec = 'line-through';

      if (selectedElement && onUpdateElement) onUpdateElement(selectedElement.id, { textDecoration: dec });
    }
  };

  const handleInsertTable = (rows: number, cols: number) => {
    setActivePopover(null);
    if (!onAddElement) return;

    let tableHtml = `<table style="border-collapse: collapse; width: 100%; border: 1.5px solid ${activeColor}; text-align: left; font-size: 11px;">`;
    for (let r = 0; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        const textVal = r === 0 ? `Header ${c + 1}` : 'Cell';
        tableHtml += `<td style="border: 1px solid ${activeColor}; padding: 5px; font-weight: ${r === 0 ? '800' : '500'}; background: ${r === 0 ? 'rgba(0,0,0,0.05)' : 'none'};">${textVal}</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';

    const colsArr = Array.from({ length: cols }, () => '');
    const rowsData = Array.from({ length: rows }, () => [...colsArr]);
    const columnWidths = Array.from({ length: cols }, () => 100);
    const rowHeights = Array.from({ length: rows }, () => 40);

    const newTableNode: WhiteboardElement = {
      id: `table-${Date.now()}`,
      type: 'table',
      x: window.innerWidth / 2 - (cols * 100) / 2,
      y: window.innerHeight / 2 - (rows * 40) / 2,
      width: cols * 100,
      height: rows * 40,
      color: activeColor,
      fillColor: '#ffffff',
      lineWidth: 2,
      opacity: 1,
      rowsData,
      columnWidths,
      rowHeights,
      title: `${rows}x${cols} Visual Data Grid`,
      text: `Table of size ${rows} x ${cols}`,
      contentHtml: tableHtml,
      fontFamily: fontFamily,
      fontSize: 12,
      textColor: activeColor
    };

    onAddElement(newTableNode);
  };

  const selectShape = (shape: 'rect' | 'circle' | 'triangle' | 'diamond') => {
    setActiveTool(shape);
    setShowShapesDropdown(false);
  };

  const selectLine = (lineTool: 'line' | 'arrow') => {
    setActiveTool(lineTool);
    setShowLinesDropdown(false);
  };

  const shapesActive = ['rect', 'circle', 'triangle', 'diamond'].includes(activeTool);
  const linesActive = ['line', 'arrow'].includes(activeTool);

  return (
    <div 
      ref={toolbarRef}
      className={`absolute z-40 flex gap-3 select-none transition-all duration-305 ${
        toolbarPosition === 'bottom'
          ? 'left-1/2 -translate-x-1/2 bottom-4 flex-row items-center justify-center'
          : 'left-4 top-1/2 -translate-y-1/2 flex-col'
      }`}>
      {/* Core toolbar card */}
      <div className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1.5 rounded-2xl shadow-xl flex gap-0.5 items-center ${
        toolbarPosition === 'bottom'
          ? 'flex-row h-[48px] w-auto'
          : 'flex-col w-[48px]'
      }`}>
        
        {/* 1. Cursor selector pointer */}
        <button
          onClick={() => { setActiveTool('select'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 ${
            activeTool === 'select'
              ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Select / Pointer (v)"
        >
          <MousePointer className="w-4 h-4" />
        </button>

        {/* 2. Hand Pan */}
        <button
          onClick={() => { setActiveTool('hand'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 ${
            activeTool === 'hand'
              ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Hand Pan (h)"
        >
          <Hand className="w-4 h-4" />
        </button>

        {toolbarPosition === 'bottom' ? (
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-0.5 shrink-0" />
        ) : (
          <hr className="w-6 border-slate-200 dark:border-slate-800 my-0.5 shrink-0" />
        )}

        {/* 3. Text Box tool */}
        <button
          onClick={() => { setActiveTool('text'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 ${
            activeTool === 'text'
              ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Static TextBox (t)"
        >
          <Type className="w-4 h-4" />
        </button>

        {/* 4. Sticky Notes tool */}
        <button
          onClick={() => { setActiveTool('sticky'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 ${
            activeTool === 'sticky'
              ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Sticky Post-it Note (n)"
        >
          <StickyNote className="w-4 h-4" />
        </button>

        {/* 5. Draw Shape dropdown submenu */}
        <div className="relative">
          <button
            onClick={() => {
              setShowShapesDropdown(prev => {
                const next = !prev;
                if (next) {
                  setShowLinesDropdown(false);
                  setShowColorPopover(false);
                  setActivePopover(null);
                  setShowDrawingCustomize(null);
                }
                return next;
              });
            }}
            className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 flex items-center justify-center relative ${
              shapesActive
                ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
                : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
            }`}
            title="Insert Vector Shape"
          >
            {activeTool === 'rect' && <Square className="w-4 h-4" />}
            {activeTool === 'circle' && <Circle className="w-4 h-4" />}
            {activeTool === 'triangle' && <Triangle className="w-4 h-4" />}
            {activeTool === 'diamond' && (
              <span className="font-bold text-[10px] rotate-45 border-[1.5px] border-current w-[11px] h-[11px] block" />
            )}
            {!shapesActive && <Square className="w-4 h-4" />}
          </button>

          {showShapesDropdown && (
            <div className={`absolute p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl flex gap-1.5 z-50 animate-in duration-150 ${
              toolbarPosition === 'bottom'
                ? 'bottom-12 left-1/2 -translate-x-1/2 slide-in-from-bottom-2'
                : 'left-12 top-0 slide-in-from-left-2'
            }`}>
              <button
                onClick={() => selectShape('rect')}
                className={`p-1.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-850 transition-colors ${activeTool === 'rect' ? 'bg-slate-200 dark:bg-slate-800' : ''}`}
                title="Rectangle"
              >
                <Square className="w-[18px] h-[18px]" />
              </button>
              <button
                onClick={() => selectShape('circle')}
                className={`p-1.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-850 transition-colors ${activeTool === 'circle' ? 'bg-slate-200 dark:bg-slate-800' : ''}`}
                title="Circle/Oval"
              >
                <Circle className="w-[18px] h-[18px]" />
              </button>
              <button
                onClick={() => selectShape('triangle')}
                className={`p-1.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-850 transition-colors ${activeTool === 'triangle' ? 'bg-slate-200 dark:bg-slate-800' : ''}`}
                title="Triangle"
              >
                <Triangle className="w-[18px] h-[18px]" />
              </button>
              <button
                onClick={() => selectShape('diamond')}
                className={`p-1.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-105 text-xs text-center border-none ${activeTool === 'diamond' ? 'bg-slate-200 dark:bg-slate-800' : ''}`}
                title="Flow Diamond"
              >
                <span className="font-bold text-[10px] rotate-45 border-[1.5px] border-current w-3.5 h-3.5 block mx-[1px]" />
              </button>
            </div>
          )}
        </div>

        {/* 6. Flow Arrow Connector */}
        <button
          onClick={() => {
            setActiveTool('arrow');
            setShowShapesDropdown(false);
          }}
          className={`p-2 rounded-xl transition-all cursor-pointer active:scale-95 flex items-center justify-center relative ${
            activeTool === 'arrow'
              ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Connection Arrow (a / Drag from elements)"
        >
          <ArrowUpRight className="w-4 h-4" />
        </button>

        {toolbarPosition === 'bottom' ? (
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-0.5 shrink-0" />
        ) : (
          <hr className="w-6 border-slate-200 dark:border-slate-800 my-0.5 shrink-0" />
        )}

        {/* 8. Freehand Drawings Pen */}
        <div className="relative">
          <button
            onClick={() => handleToolClick('pen')}
            className={`rounded-xl transition-all cursor-pointer active:scale-95 flex items-center justify-center ${
              activeTool === 'pen'
                ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
                : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
            }`}
            title="Freehand Ink Pen (p) · Click again or Right-click to customize"
            style={{ width: 34, height: 34, flexShrink: 0 }}
          >
            <PenTool style={{ width: 16, height: 16, display: 'block', flexShrink: 0 }} />
          </button>
          
          {showDrawingCustomize === 'pen' && (
            <div className={`absolute z-50 shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-100 w-[300px] ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2'
                : 'left-14 top-0'
            }`}>
              <BrushSelector
                color={activeColor}
                setColor={setActiveColor}
                lineWidth={brushWidth || 4}
                setLineWidth={setBrushWidth || (() => {})}
                brushColors={brushColors || PRESET_COLORS}
                isActive={true}
              />
            </div>
          )}

          {rightClickHintTool === 'pen' && (
            <div className={`absolute whitespace-nowrap text-[10px] font-semibold text-white bg-slate-800 dark:bg-slate-700 px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none animate-in fade-in slide-in-from-left-2 duration-200 ${
              toolbarPosition === 'bottom' ? 'bottom-12 left-1/2 -translate-x-1/2' : 'left-12 top-1/2 -translate-y-1/2'
            }`}>
              Right-click to customize
              <div className={`absolute w-2 h-2 bg-slate-800 dark:bg-slate-700 rotate-45 ${
                toolbarPosition === 'bottom' ? '-bottom-1 left-1/2 -translate-x-1/2' : 'top-1/2 -translate-y-1/2 -left-1'
              }`} />
            </div>
          )}
        </div>

        {/* 9. Highlighter Tool */}
        <div className="relative">
          <button
            onClick={() => handleToolClick('highlighter')}
            className={`rounded-xl transition-all cursor-pointer active:scale-95 flex items-center justify-center ${
              activeTool === 'highlighter'
                ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
                : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
            }`}
            title="Semi-transparent Highlighter · Click again or Right-click to customize"
            style={{ width: 34, height: 34, flexShrink: 0 }}
          >
            <div className="relative flex items-center justify-center">
              <Highlighter style={{ width: 16, height: 16, display: 'block', flexShrink: 0 }} />
              <div className="absolute inset-0 bg-yellow-400/20 rounded-md scale-90" />
            </div>
          </button>
          
          {showDrawingCustomize === 'highlighter' && (
            <div className={`absolute z-50 shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-100 w-[300px] ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2'
                : 'left-14 top-0'
            }`}>
              <BrushSelector
                color={activeColor}
                setColor={setActiveColor}
                lineWidth={brushWidth || 12}
                setLineWidth={setBrushWidth || (() => {})}
                brushColors={brushColors || PRESET_COLORS}
                isActive={true}
              />
            </div>
          )}

          {rightClickHintTool === 'highlighter' && (
            <div className={`absolute whitespace-nowrap text-[10px] font-semibold text-white bg-slate-800 dark:bg-slate-700 px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none animate-in fade-in slide-in-from-left-2 duration-200 ${
              toolbarPosition === 'bottom' ? 'bottom-12 left-1/2 -translate-x-1/2' : 'left-12 top-1/2 -translate-y-1/2'
            }`}>
              Right-click to customize
              <div className={`absolute w-2 h-2 bg-slate-800 dark:bg-slate-700 rotate-45 ${
                toolbarPosition === 'bottom' ? '-bottom-1 left-1/2 -translate-x-1/2' : 'top-1/2 -translate-y-1/2 -left-1'
              }`} />
            </div>
          )}
        </div>

        {/* 10. Color palette / Paint fill bucket */}
        <div className="relative">
          <button
            onClick={() => {
              setShowColorPopover(prev => {
                const next = !prev;
                if (next) {
                  setShowShapesDropdown(false);
                  setShowLinesDropdown(false);
                  setActivePopover(null);
                  setShowDrawingCustomize(null);
                }
                return next;
              });
            }}
            className="p-2 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 hover:bg-slate-100 dark:hover:bg-slate-850 flex flex-col items-center gap-[1px]"
            title="Whiteboard Color Brush (Active Bucket)"
          >
            <PaintBucket className="w-[18px] h-[18px] text-slate-700 dark:text-slate-300" />
            <div className="w-4 h-1 rounded-sm mt-0.5" style={{ backgroundColor: activeColor }} />
          </button>

          {showColorPopover && (
            <div className={`absolute bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-2xl shadow-2xl w-[190px] z-50 animate-in duration-150 ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2 slide-in-from-bottom-2'
                : 'left-14 bottom-0 slide-in-from-left-2'
            }`}>
              <span className="text-[10px] uppercase font-mono tracking-widest text-slate-400 block mb-2 font-bold">Active Paint Color</span>
              <div className="grid grid-cols-4 gap-1.5">
                {PRESET_COLORS.map((col) => (
                  <button
                    key={col}
                    onClick={() => {
                      setActiveColor(col);
                      setShowColorPopover(false);
                      // If shape selected, will auto update element inside parent
                    }}
                    className={`w-6.5 h-6.5 rounded-full border transform hover:scale-115 active:scale-90 transition-transform ${
                      activeColor === col ? 'ring-2 ring-blue-500 border-white' : 'border-slate-200/50 dark:border-slate-800'
                    }`}
                    style={{ backgroundColor: col }}
                    title={col}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 11. Eraser Tool */}
        <div className="relative">
          <button
            onClick={() => handleToolClick('eraser')}
            className={`rounded-xl transition-all cursor-pointer active:scale-95 flex items-center justify-center ${
              activeTool === 'eraser'
                ? 'bg-blue-600 text-white shadow-lg -translate-y-[3px]'
                : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
            }`}
            title="Eraser Point/Stroke deletes items (e) · Click again or Right-click to customize"
            style={{ width: 34, height: 34, flexShrink: 0 }}
          >
            <Eraser style={{ width: 16, height: 16, display: 'block', flexShrink: 0 }} />
          </button>
          
          {showDrawingCustomize === 'eraser' && (
            <div className={`absolute z-50 shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-100 w-[300px] ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2'
                : 'left-14 top-0'
            }`}>
              <EraserControl
                lineWidth={eraserWidth || 12}
                setLineWidth={setEraserWidth || (() => {})}
                isActive={true}
              />
            </div>
          )}

          {rightClickHintTool === 'eraser' && (
            <div className={`absolute whitespace-nowrap text-[10px] font-semibold text-white bg-slate-800 dark:bg-slate-700 px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none animate-in fade-in slide-in-from-left-2 duration-200 ${
              toolbarPosition === 'bottom' ? 'bottom-12 left-1/2 -translate-x-1/2' : 'left-12 top-1/2 -translate-y-1/2'
            }`}>
              Right-click to customize
              <div className={`absolute w-2 h-2 bg-slate-800 dark:bg-slate-700 rotate-45 ${
                toolbarPosition === 'bottom' ? '-bottom-1 left-1/2 -translate-x-1/2' : 'top-1/2 -translate-y-1/2 -left-1'
              }`} />
            </div>
          )}
        </div>

        {toolbarPosition === 'bottom' ? (
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-0.5 shrink-0" />
        ) : (
          <hr className="w-6 border-slate-200 dark:border-slate-800 my-0.5 shrink-0" />
        )}

        {/* 12. Message comment tool */}
        <button
          onClick={() => { setActiveTool('comment'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 ${
            activeTool === 'comment'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Drop Comment Pin (c)"
        >
          <MessageSquare className="w-4 h-4" />
        </button>

        {/* 13. Image uploader */}
        <button
          onClick={() => { setActiveTool('image'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 ${
            activeTool === 'image'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Attach Bitmap Image (i)"
        >
          <ImageIcon className="w-4 h-4" />
        </button>

        {/* 14. Table Grid Creator */}
        <button
          onClick={() => { setActiveTool('table'); }}
          className={`p-2 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 ${
            activeTool === 'table'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-600 dark:text-slate-355 hover:bg-slate-100 dark:hover:bg-slate-850'
          }`}
          title="Create Table Grid (t)"
        >
          <Table className="w-4 h-4" />
        </button>

        {/* Divider */}
        {toolbarPosition === 'bottom' ? (
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-0.5 shrink-0" />
        ) : (
          <hr className="w-6 border-slate-200 dark:border-slate-800 my-0.5 shrink-0" />
        )}

        {/* Aa Text Options menu */}
        <div className="relative">
          <button
            onClick={() => togglePopover('textOptions')}
            className={`p-2 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 flex items-center justify-center ${
              activePopover === 'textOptions' ? 'bg-blue-100 text-blue-600' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-850'
            }`}
            title="Font formatting & options (Aa)"
          >
            <CaseSensitive className="w-4 h-4" />
          </button>

          {activePopover === 'textOptions' && (
            <div className={`absolute bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4.5 rounded-2xl shadow-2xl z-50 w-[240px] flex flex-col gap-3.5 text-left animate-in fade-in duration-150 ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2'
                : 'left-14 bottom-0'
            }`}>
              {/* Font family layout block */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 block mb-1">Font family</label>
                <div className="relative">
                  <select
                    value={fontFamily}
                    onChange={(e) => {
                      applyStyle('fontFamily', e.target.value);
                    }}
                    className="w-full text-xs font-semibold bg-slate-50 dark:bg-slate-850 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-750 px-2.5 py-2 rounded-xl cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none font-sans"
                    style={{ fontFamily: fontFamily }}
                  >
                    <option value='"Inter", sans-serif'>Inter UI</option>
                    <option value='"JetBrains Mono", monospace'>JetBrains Mono</option>
                    <option value='"Space Grotesk", sans-serif'>Space Grotesk</option>
                    <option value='"Outfit", sans-serif'>Outfit Sharp</option>
                    <option value='"Playfair Display", serif'>Playfair Display</option>
                    <option value='"Fira Code", monospace'>Fira Code</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </div>
              </div>

              {/* Style buttons Row */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 block mb-1">Style</label>
                <div className="flex border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-xs divide-x dark:divide-slate-800">
                  <button
                    onClick={() => handleToggleStyle('bold')}
                    className={`flex-1 py-2 text-xs font-black cursor-pointer flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors ${
                      isBold ? 'bg-slate-100 dark:bg-slate-800 text-blue-600' : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <Bold className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleToggleStyle('italic')}
                    className={`flex-1 py-2 text-xs italic cursor-pointer flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors ${
                      isItalic ? 'bg-slate-100 dark:bg-slate-800 text-blue-600' : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <Italic className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleToggleStyle('underline')}
                    className={`flex-1 py-2 text-xs underline underline-offset-2 cursor-pointer flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors ${
                      isUnderline ? 'bg-slate-100 dark:bg-slate-800 text-blue-600' : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <Underline className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleToggleStyle('strikethrough')}
                    className={`flex-1 py-2 text-xs line-through cursor-pointer flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-850 transition-colors ${
                      isStrikethrough ? 'bg-slate-100 dark:bg-slate-800 text-blue-600' : 'text-slate-600 dark:text-zinc-400'
                    }`}
                  >
                    <Strikethrough className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Font Size & Line Height inputs */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 block mb-0.5">Font size</label>
                  <input
                    type="number"
                    value={fontSize}
                    onChange={(e) => {
                      applyStyle('fontSize', e.target.value);
                    }}
                    className="w-full text-xs font-bold font-mono bg-slate-50 dark:bg-slate-850 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-750 px-2.5 py-1.5 rounded-xl text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 block mb-0.5">Line ht</label>
                  <input
                    type="number"
                    value={lineHeight}
                    onChange={(e) => {
                      applyStyle('lineHeight', e.target.value);
                    }}
                    className="w-full text-xs font-bold font-mono bg-slate-50 dark:bg-slate-850 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-750 px-2.5 py-1.5 rounded-xl text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Text Align dropdown */}
        <div className="relative">
          <button
            onClick={() => togglePopover('align')}
            className={`p-2.5 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 flex items-center justify-center ${
              activePopover === 'align' ? 'bg-blue-100 text-blue-600' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-850'
            }`}
            title="Alignment"
          >
            {textAlign === 'left' ? (
              <AlignLeft className="w-[18px] h-[18px]" />
            ) : textAlign === 'center' ? (
              <AlignCenter className="w-[18px] h-[18px]" />
            ) : textAlign === 'right' ? (
              <AlignRight className="w-[18px] h-[18px]" />
            ) : (
              <AlignJustify className="w-[18px] h-[18px]" />
            )}
          </button>

          {activePopover === 'align' && (
            <div className={`absolute bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1 rounded-xl shadow-2xl z-50 w-[110px] flex flex-col animate-in duration-105 ${
              toolbarPosition === 'bottom'
                ? 'bottom-14 left-1/2 -translate-x-1/2'
                : 'left-14 bottom-0'
            }`}>
              {(['left', 'center', 'right', 'justify'] as const).map((dir) => (
                <button
                  key={dir}
                  onClick={() => {
                    applyStyle('textAlign', dir);
                    setActivePopover(null);
                  }}
                  className={`py-1.5 px-2.5 text-xs text-left rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 cursor-pointer capitalize font-semibold ${
                    textAlign === dir ? 'text-blue-600 bg-blue-50/20' : 'text-slate-650 dark:text-zinc-300'
                  }`}
                >
                  {dir === 'left' ? (
                    <AlignLeft className="w-3.5 h-3.5" />
                  ) : dir === 'center' ? (
                    <AlignCenter className="w-3.5 h-3.5" />
                  ) : dir === 'right' ? (
                    <AlignRight className="w-3.5 h-3.5" />
                  ) : (
                    <AlignJustify className="w-3.5 h-3.5" />
                  )}
                  <span>{dir}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grid / Table creator insertion item */}
        <div className="relative">
          <button
            onClick={() => togglePopover('table')}
            className={`p-2.5 rounded-xl transition-all cursor-pointer hover:scale-105 active:scale-95 flex items-center justify-center ${
              activePopover === 'table' ? 'bg-blue-100 text-blue-600' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-850'
            }`}
            title="Insert rich grid/table layout"
          >
            <Table2 className="w-[18px] h-[18px]" />
          </button>

          {activePopover === 'table' && (
            <div
              ref={popupRef}
              className={`absolute bg-white border border-slate-200 rounded-lg p-3 shadow-xl select-none dark:bg-slate-900 dark:border-slate-800 z-50 animate-in duration-100 ${
                toolbarPosition === 'bottom'
                  ? 'bottom-14 left-1/2 -translate-x-1/2'
                  : 'left-14 top-0'
              }`}
              id="table-picker-popup"
              role="dialog"
              aria-label="Insert table"
              onMouseDown={(event) => event.stopPropagation()}
              style={{ width: '220px' }}
            >
              <div
                className="touch-none"
                id="table-matrix-grid-container"
                onPointerMove={handleGridPointerMove}
              >
                <div
                  className="grid gap-[3px]"
                  style={{ 
                    gridTemplateColumns: 'repeat(10, 16px)',
                    justifyContent: 'center'
                  }}
                >
                  {Array.from({ length: 50 }).map((_, index) => {
                    const row = Math.floor(index / 10) + 1;
                    const col = (index % 10) + 1;
                    const isSelected = row <= hoverGridRow && col <= hoverGridCol;

                    return (
                      <button
                        key={`${row}-${col}`}
                        type="button"
                        data-table-cell="true"
                        data-row={row}
                        data-col={col}
                        onPointerEnter={() => {
                          setHoverGridRow(row);
                          setHoverGridCol(col);
                        }}
                        onPointerDown={(event) => handleCellPointerDown(event, row, col)}
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '1px',
                          border: '1px solid',
                          borderColor: isSelected ? '#3b82f6' : '#e5e7eb',
                          backgroundColor: isSelected ? '#dbeafe' : '#f9fafb',
                          cursor: 'pointer',
                          transition: 'all 100ms ease',
                        }}
                        className="dark:bg-slate-950 dark:border-slate-800"
                        title={`${col} x ${row} table`}
                        aria-label={`${col} columns by ${row} rows`}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="mt-2 text-center text-xs font-bold text-slate-700 dark:text-slate-350">
                {hoverGridCol} x {hoverGridRow}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
